import { BREAKPOINTS, COLORS, ContentIcons, SystemIcons } from '@laerdal/life-react-components';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface NoAssetsProps {
  productLineName?: string;
}

const Container = styled.div`
  display: flex;
  padding: var(--Margin-and-padding-padding-large, 32px);
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex: 1 0 0;
  border-radius: 8px;
  border: 0 solid var(--color-neutral-100, #E5E5E5);
  background: var(--Neutral-020-tint, #FAFAFA);
  width: 100%;
  box-sizing: border-box;
`;

const MessageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const IconCircle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--Neutral-050-tint, #F2F2F2);
  flex-shrink: 0;
`;

const MessageText = styled.div`
  color: var(--Neutral-700-shade, #4D4D4D);
  font-feature-settings: 'liga' off;
  font-family: Lato;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const CardsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  ${BREAKPOINTS.LARGE} {
    flex-direction: row;
  }
`;

const ActionCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  border-radius: 8px;
  border: 1px solid var(--Neutral-200-tint, #CCC);
  background: var(--Base-White, #FFF);
  cursor: pointer;

  &:hover {
    background: var(--Primary-020-tint, #F1FBFE);
  }

  &:active {
    background: var(--Primary-100-tint, #D4E9F2);
  }
`;

const CardHeader = styled.div`
  display: flex;
  padding: 24px 24px 12px 24px;
  align-items: center;
  align-self: stretch;
  gap: 8px;
`;

const CardTitle = styled.div`
  color: var(--Base-Black, #1F1F1F);
  font-feature-settings: 'liga' off;
  font-family: var(--font-family, Lato);
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
`;

const CardBody = styled.div`
  display: flex;
  padding: 0 24px 24px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const CardDescription = styled.div`
  color: var(--Neutral-600-shade, #666);
  font-feature-settings: 'liga' off;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

interface ActionItem {
  icon: React.ReactNode;
  title: string;
  description: string;
  url: string;
}

const NoAssets: React.FC<NoAssetsProps> = ({ productLineName }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('landingPage');

  const actions: ActionItem[] = [
    {
      icon: <SystemIcons.ShoppingCart size="24px" color={COLORS.positive_500} />,
      title: t('Go to shop'),
      description: t('Browse the Laerdal product catalog to purchase products and accessories for your shopping needs.'),
      url: 'https://laerdal.com/us/shop/?category=Everything&sortBy=0',
    },
    {
      icon: <SystemIcons.Add size="24px" color={COLORS.primary_500} />,
      title: t('Register a product'),
      description: t('Register a serial number from your organisation and get access after approval.'),
      url: '/case/create/technical/assetMissing',
    },
    {
      icon: <SystemIcons.Mail size="24px" color={COLORS.primary_500} />,
      title: t('Request access'),
      description: t('If you are already using Laerdal products, please request access to view and manage them.'),
      url: '/case/create/technical/connect',
    },
  ];

  const handleClick = (url: string) => {
    if (url.startsWith('http')) {
      window.location.href = url;
    } else {
      navigate(url);
    }
  };

  return (
    <Container>
      <MessageRow>
        <IconCircle>
          <ContentIcons.Manikin size="32" color={COLORS.neutral_700} />
        </IconCircle>
        <MessageText>
          {t('There are no {{productLineName}} products at your organisation to display for you.', { productLineName })}
        </MessageText>
      </MessageRow>
      <CardsRow>
        {actions.map((action, index) => (
          <ActionCard key={index} onClick={() => handleClick(action.url)} role="button" tabIndex={0}>
            <CardHeader>
              {action.icon}
              <CardTitle>{action.title}</CardTitle>
            </CardHeader>
            <CardBody>
              <CardDescription>{action.description}</CardDescription>
            </CardBody>
          </ActionCard>
        ))}
      </CardsRow>
    </Container>
  );
};

export default NoAssets;
